import classes from './portfolioSkeleton.module.css';

/**
 * Portfolio Overview loading skeleton.
 *
 * Mirrors the live layout one-to-one so the loaded → loading swap is a
 * silent fade rather than a layout reflow:
 *   Header   — title bar + subtitle line
 *   KPI strip — 5 stat-card shimmers in a row
 *   Body     — LEFT: a column of project-card shimmers (photo panel +
 *              the Performance bars and the Milestones section)
 *              RIGHT: a "Cross-Project Alerts" rail with a header + a
 *              longer list of alert-row shimmers
 *
 * The portfolio view is deliberately fuller than the single-project
 * dashboard, so it renders MORE project cards and a LONGER alerts list.
 *
 * Reuses the dashboard skeleton's shimmer technique (single shared
 * keyframe + per-element `--wave` stagger) so the two surfaces pulse
 * identically. Block widths are jagged on purpose — uniform shimmer
 * reads as a generic placeholder, varied widths read as "real content".
 */
export function PortfolioSkeleton() {
  return (
    <div className={classes.page} aria-busy="true" data-testid="portfolio-loading">
      {/* KPI strip — 5 stat cards */}
      <div className={classes.kpiStrip}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={classes.kpiTile}>
            <span className={`${classes.shimmer} ${classes.kpiLabel}`} />
            <span className={`${classes.shimmer} ${classes.kpiValue}`} />
            <span className={`${classes.shimmer} ${classes.kpiSub}`} />
          </div>
        ))}
      </div>

      {/* Body — project cards (left) + alerts rail (right) */}
      <div className={classes.body}>
        <section className={classes.projects}>
          {PROJECT_NAME_WIDTHS.map((w, i) => (
            <ProjectCardShimmer key={i} nameWidth={w} />
          ))}
        </section>
        <AlertsRailShimmer />
      </div>
    </div>
  );
}

/* The portfolio shows several sites at once — render more cards than the
   single-project dashboard. Varied title widths keep the column natural. */
const PROJECT_NAME_WIDTHS = ['44%', '36%', '52%', '40%'];

/* Project card — photo panel on the left (name + manager shimmer at its
   foot), then Performance (four bars) and Milestones (the strip and two
   boxes). Lays out by its own width, like ProjectCard. */
function ProjectCardShimmer({ nameWidth }: { nameWidth: string }) {
  return (
    <article className={classes.card}>
      <div className={classes.cardLayout}>
        <div className={classes.cardPhoto}>
          <span className={`${classes.shimmer} ${classes.cardName}`} style={{ width: nameWidth }} />
          <span className={`${classes.shimmer} ${classes.cardManager}`} />
        </div>

        <div className={classes.cardBody}>
          <div className={classes.cardBars}>
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className={`${classes.shimmer} ${classes.cardBar}`} />
            ))}
          </div>
          <span className={classes.cardDivider} />
          <div className={classes.cardMilestones}>
            <span className={`${classes.shimmer} ${classes.cardMilestoneBar}`} />
            <div className={classes.cardBoxes}>
              <span className={`${classes.shimmer} ${classes.cardBox}`} />
              <span className={`${classes.shimmer} ${classes.cardBox}`} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/* Cross-Project Alerts rail — header + a longer list of alert rows
   (the portfolio aggregates alerts across every site, so it's deeper
   than the single-project feed). */
const ALERT_TEXT_WIDTHS = ['82%', '64%', '90%', '70%', '78%', '58%'];

function AlertsRailShimmer() {
  return (
    <aside className={classes.rail}>
      <div className={classes.railTitleRow}>
        <span className={`${classes.shimmer} ${classes.railTitleIcon}`} />
        <span className={`${classes.shimmer} ${classes.railTitle}`} />
      </div>
      <div className={classes.railList}>
        {ALERT_TEXT_WIDTHS.map((w, i) => (
          <div key={i} className={classes.alertItem}>
            <div className={classes.alertHead}>
              <span className={`${classes.shimmer} ${classes.alertIcon}`} />
              <span className={`${classes.shimmer} ${classes.alertSite}`} />
              <span className={`${classes.shimmer} ${classes.alertAgo}`} />
            </div>
            <span className={`${classes.shimmer} ${classes.alertText}`} style={{ width: w }} />
          </div>
        ))}
      </div>
    </aside>
  );
}
